import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { alpha } from '@mui/material/styles';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Badge from '@mui/material/Badge';
import Fab from '@mui/material/Fab';
import Typography from '@mui/material/Typography';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import styles from '../../../ReviewEmployeeAccess.module.scss';
import Card from '../../../../../../../../components/UI/MDC/Card/Card';
import { Headline4 } from '../../../../../../../../components/UI/MDC/Typography/Typography';
import ReviewApps from './components/ReviewApps';
import ReviewMobileDevices from './components/ReviewMobileDevices';
import ReviewBadges from './components/ReviewBadges';
import ReviewServiceAccounts from './components/ReviewServiceAccounts';
import StaffMemberInformationCard from '../../../../../../components/StaffMemberInformation/StaffMemberInformationCard';
import UarShoppingCartModal from 'pages/AnnualUserAccessReview/components/UarShoppingCartModal';
import usePersistentUarActionCart from 'pages/AnnualUserAccessReview/components/usePersistentUarActionCart';
import { PAGE_ROUTES } from 'pages/AnnualUserAccessReview/_const';
import axiosInstance from 'services/axios/axios';
import { WrapImsPacket } from 'store/utility';
import { useHistory } from 'react-router-dom';
import * as uarActions from 'store/uar/actions';
import { selectProcessedRequests } from 'store/uar/selectors';

const UAR_SUBMIT_ACTION_ITEMS_EVENT = 'uar.submitActionItems';
const UAR_SUBMIT_ACTION_ITEMS_COMPLETE_EVENT =
	'uar.submitActionItems.complete';
const UAR_CART_COUNT_CHANGE_EVENT = 'uar.cart.count.change';
const UAR_OPEN_SHOPPING_CART_EVENT = 'uar.openShoppingCart';

const ReviewInformation = ({ formik }) => {
	const dispatch = useDispatch();
	const [sCartOpen, setCartOpen] = useState(false);
	const [sIsSubmitting, setIsSubmitting] = useState(false);
	const [sProcessedByStaff, setProcessedByStaff] = useState({});
	const history = useHistory();
	const processedRequests = useSelector(selectProcessedRequests);
	const {
		sCartItems,
		addOrToggleAction,
		clearCart,
		removeCartItem,
		getSelectedAction,
		cartCount,
	} = usePersistentUarActionCart();

	const onQueueAppActionHandler = (staffMember, app, action) => {
		const staffMemberId = staffMember?.fiId || staffMember?.esoFiId;
		if (staffMemberId && app?.id && action) {
			addOrToggleAction({
				itemKey: `${staffMemberId}:${app.id}`,
				staffMemberId,
				staffMemberName:
					`${staffMember?.lastName || ''}, ${staffMember?.firstName || ''}`.trim(),
				populationLabel:
					staffMember?.type === 'nonEmployee'
						? 'Non-Employee'
						: 'Employee',
				appId: app.id,
				appName: app.name,
				username: app.username,
				action,
			});
		}
	};

	const onSubmitCartHandler = useCallback(async () => {
		const onSubmitActionItemsCompleteHandler = () => {
			window.dispatchEvent(
				new Event(UAR_SUBMIT_ACTION_ITEMS_COMPLETE_EVENT)
			);
		};

		if (sCartItems.length > 0) {
			const currentUser = formik?.values?.currentUser;
			const currentUserEsoFiId =
				currentUser?.esoFiId || currentUser?.ID || currentUser?.id;

			if (currentUserEsoFiId) {
				setIsSubmitting(true);
				try {
					const groupedByStaff = sCartItems.reduce((acc, item) => {
						const key = item.staffMemberId;
						if (!acc[key]) {
							acc[key] = [];
						}
						acc[key].push(item);
						return acc;
					}, {});

					const modifyQueueEntries = [];
					const processedRequests = [];

					for (const staffMemberId of Object.keys(groupedByStaff)) {
						const staffItems = groupedByStaff[staffMemberId];
						const hasModifyAction = staffItems.some(item => {
							return item.action === 'MODIFY';
						});

						const apps = staffItems.map(item => ({
							action: item.action,
							data: {
								id: item.appId,
							},
						}));

						const request = {
							loggedInUser: {
								esoFiId: currentUserEsoFiId,
							},
							target: {
								esoFiId: Number(staffMemberId),
							},
							apps,
						};

						const response = await axiosInstance.post(
							'/api/UserAccessReview/AppCart/Reserve',
							WrapImsPacket(request)
						);

						if (
							response?.data?.responseCode !== 200 ||
							!response?.data?.payload
						) {
							throw new Error('Unable to reserve one or more cart actions.');
						}

						if (!hasModifyAction) {
							const completionPacket = WrapImsPacket({ Completed: true });
							const completionResponse = await axiosInstance.patch(
								`/api/Applications/Requests/${response.data.payload}`,
								completionPacket
							);

							if (
								completionResponse?.status !== 200 ||
								completionResponse?.data?.responseCode !== 200
							) {
								throw new Error('Unable to complete one or more revoke requests.');
							}
						}

						const appActions = staffItems.map(item => {
							const processedAt = new Date().toISOString();
							return {
								action: item.action,
								appId: item.appId,
								appName: item.appName,
								processedAt,
							};
						});

						processedRequests.push({
							trackingNumber: response.data.payload,
							staffMemberId: Number(staffMemberId),
							staffMemberName:
								staffItems?.[0]?.staffMemberName ||
								String(staffMemberId),
							appActions,
						});

						if (hasModifyAction) {
							modifyQueueEntries.push({
								trackingNumber: response.data.payload,
								staffMemberId: Number(staffMemberId),
								staffMemberName:
									staffItems?.[0]?.staffMemberName ||
									String(staffMemberId),
								appActions,
							});
						}
					}

					dispatch(uarActions.setProcessedRequests(processedRequests));

					if (modifyQueueEntries.length > 0) {
						const currentQueueItem = modifyQueueEntries[0];
						const selectedEsoFiIds = modifyQueueEntries
							.map(entry => Number(entry?.staffMemberId))
							.filter(id => Number.isFinite(id) && id > 0);
						const actionFlowQuery = new URLSearchParams({
							tn: currentQueueItem.trackingNumber,
							uarSource: 'annualReview',
							uarTargetName:
								currentQueueItem.staffMemberName ||
								String(currentQueueItem.staffMemberId || ''),
							uarStep: '1',
							uarTotal: String(modifyQueueEntries.length),
							esoFiId: selectedEsoFiIds.join(','),
						});

						dispatch(uarActions.setModifyQueue(modifyQueueEntries));

						setCartOpen(false);
						history.push(
							`${PAGE_ROUTES.REVIEW_ACTION_FLOW}?${actionFlowQuery.toString()}`
						);
					} else {
						clearCart();
						setCartOpen(false);
						history.push(
							`${PAGE_ROUTES.REVIEW_ACTION_FLOW}?view=processed`
						);
					}
				} catch {
				} finally {
					setIsSubmitting(false);
					onSubmitActionItemsCompleteHandler();
				}
			} else {
				onSubmitActionItemsCompleteHandler();
			}
		} else {
			onSubmitActionItemsCompleteHandler();
		}
	}, [clearCart, dispatch, formik, history, sCartItems]);

	useEffect(() => {
		const onSubmitActionItemsEventHandler = () => {
			onSubmitCartHandler();
		};

		const onOpenShoppingCartEventHandler = () => {
			setCartOpen(true);
		};

		window.addEventListener(
			UAR_SUBMIT_ACTION_ITEMS_EVENT,
			onSubmitActionItemsEventHandler
		);

		window.addEventListener(
			UAR_OPEN_SHOPPING_CART_EVENT,
			onOpenShoppingCartEventHandler
		);

		return () => {
			window.removeEventListener(
				UAR_SUBMIT_ACTION_ITEMS_EVENT,
				onSubmitActionItemsEventHandler
			);
			window.removeEventListener(
				UAR_OPEN_SHOPPING_CART_EVENT,
				onOpenShoppingCartEventHandler
			);
		};
	}, [onSubmitCartHandler])

	useEffect(() => {
		window.dispatchEvent(
			new CustomEvent(UAR_CART_COUNT_CHANGE_EVENT, {
				detail: {
					count: cartCount,
				},
			})
		);
	}, [cartCount]);

	useEffect(() => {
		const nextProcessedByStaff = {};

		if (processedRequests && processedRequests.length > 0) {
			processedRequests.forEach(entry => {
				const staffKey = String(entry?.staffMemberId || '');
				const trackingNumber = entry?.trackingNumber;
				const appActions = Array.isArray(entry?.appActions)
					? entry.appActions
					: [];

				if (staffKey && trackingNumber) {
					const actionByApp = appActions.reduce((acc, appAction) => {
						const appIdKey = String(appAction?.appId || '');
						const appNameKey = String(
							appAction?.appName || ''
						).toLowerCase();
						const marker = {
							trackingNumber,
							action: appAction?.action || '',
							processedAt: appAction?.processedAt || '',
						};

						if (appIdKey) {
							acc.byId[appIdKey] = marker;
						}

						if (appNameKey) {
							acc.byName[appNameKey] = marker;
						}

						return acc;
					}, { byId: {}, byName: {} });

					nextProcessedByStaff[staffKey] = actionByApp;
				}
			});
		}

		setProcessedByStaff(nextProcessedByStaff);
	}, [processedRequests]);

	return (
		<>
			<Card xlarge>
				{cartCount > 0 ? (
					<Fab
						variant='extended'
						onClick={() => setCartOpen(true)}
						sx={theme => ({
							width: 'fit-content',
							minWidth: 0,
							maxWidth: 'calc(100vw - 32px)',
							position: 'fixed',
							left: 'unset !important',
							right: { xs: '16px !important', sm: '24px !important' },
							top: {
								xs: 'calc(env(safe-area-inset-top, 0px) + 76px) !important',
								sm: 'calc(env(safe-area-inset-top, 0px) + 80px) !important',
							},
							zIndex: theme.zIndex.appBar - 1,
							borderRadius: 10,
							border: `2px solid ${theme.palette.common.white}`,
							color: theme.palette.primary.contrastText,
							background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
							boxShadow: `0 8px 24px ${alpha(
								theme.palette.primary.main,
								cartCount > 0 ? 0.42 : 0.28
							)}`,
							fontWeight: 700,
							fontSize: 14,
							textTransform: 'none',
							pl: 1.5,
							pr: 2,
							gap: 0.75,
							'&:hover': {
								background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.dark} 100%)`,
								boxShadow: `0 12px 32px ${alpha(
									theme.palette.primary.main,
									0.45
								)}`,
							},
							'@keyframes cartPulse': {
								'0%': {
									transform: 'scale(1)',
								},
								'50%': {
									transform: 'scale(1.04)',
								},
								'100%': {
									transform: 'scale(1)',
								},
							},
							animation:
								cartCount > 0
									? 'cartPulse 1.6s ease-in-out infinite'
									: 'none',
						})}
					>
						<Badge badgeContent={cartCount} color='error' max={99}>
							<ShoppingCartIcon />
						</Badge>
						Shopping Cart
					</Fab>
				) : null}
				<Headline4>Review Employee Information</Headline4>

				{formik?.values?.staffMembersForReview?.map(
					(staffMember, index) => {
						const staffMemberId =
							staffMember?.fiId || staffMember?.esoFiId;
						const processedActionsByApp =
							sProcessedByStaff[String(staffMemberId)] || {
								byId: {},
								byName: {},
							};

						return (
							<StaffMemberInfo
								staffMember={staffMember}
								index={index}
								formik={formik}
								key={staffMemberId}
								processedActionsByApp={processedActionsByApp}
								onQueueAppAction={onQueueAppActionHandler}
								getQueuedAction={getSelectedAction}
							/>
						);
					}
				)}
			<UarShoppingCartModal
					open={sCartOpen}
					onClose={() => setCartOpen(false)}
					cartItems={sCartItems}
					onClear={clearCart}
					onRemove={removeCartItem}
					onSubmit={onSubmitCartHandler}
					isSubmitting={sIsSubmitting}
				/>
			</Card>
		</>
	);
};

export default ReviewInformation;

//internal
const StaffMemberInfo = ({
	staffMember,
	index,
	formik,
	processedActionsByApp,
	onQueueAppAction,
	getQueuedAction,
}) => {
	return (
		<>
			<div className={styles.margin_top_small}>
				<Accordion className={styles.accordion} defaultExpanded>
					<AccordionSummary
						className={styles.accordion_summary}
						expandIcon={
							<ArrowDownwardIcon
								className={styles.accordion_summary}
							/>
						}
						aria-controls={'StaffMember-content-' + index}
						id={'StaffMember-header-' + index}
					>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 8,
								width: '100%',
							}}
						>
							<Typography>
								{staffMember?.lastName +
									', ' +
									staffMember?.firstName}
							</Typography>
						</div>
					</AccordionSummary>
					<AccordionDetails className={styles.accordion_details}>
						<StaffMemberInformationCard
							staffMember={staffMember}
							formik={formik}
						/>
						{/* apps */}
						<ReviewApps
							staffMember={staffMember}
							index={index}
							formik={formik}
							processedActionsByApp={processedActionsByApp}
							onQueueAppAction={onQueueAppAction}
							getQueuedAction={getQueuedAction}
						/>
						{/*Mobile Devices */}
						<ReviewMobileDevices
							staffMember={staffMember}
							index={index}
							formik={formik}
						/>
						{/*Badges */}
						<ReviewBadges
							staffMember={staffMember}
							index={index}
							formik={formik}
						/>
						{/*Service Accounts */}
						<ReviewServiceAccounts
							staffMember={staffMember}
							index={index}
							formik={formik}
						/>
					</AccordionDetails>
				</Accordion>
			</div>
		</>
	);
};
